import {
  defaultConsents,
  getCookie,
  getUserConsent,
  setCookie,
  shiftDomain,
} from "@fruugo/utilities";
import { Sku, CartItem, ItemListContext } from "../../types";

const { currentCurrencyCode } = window;

export function sendAddToCartEvent(
  sku: Sku | null,
  quantity: number,
  customEventDimensions?: Gtag.CustomParams,
  customItemDimensions?: Gtag.CustomParams
) {
  if (!sku) return;
  gtag("event", "add_to_cart", {
    currency: sku.price.currencyCode,
    value: getSkuPrice(sku) * quantity,
    items: [createGAItem(sku, quantity, customItemDimensions)],
    euro_value_incl_tax: getSkuPriceInEuros(sku) * quantity,
    value_incl_tax: getSkuPriceIncludingTax(sku) * quantity,
    voucher_discount_applied: sku.price?.voucherDiscountApplied,
    handling_charge: sku.shippingHandlingChargeAmount,
    duties: sku.shippingDutyAmount,
    ...customEventDimensions,
  });
}

export function sendViewItemEvent(
  sku: Sku | null,
  customItemDimensions?: Gtag.CustomParams
) {
  if (!sku) return;
  const quantity = 1;

  gtag("event", "view_item", {
    currency: sku.price.currencyCode,
    value: getSkuPrice(sku),
    items: [createGAItem(sku, quantity, customItemDimensions)],
    euro_value_incl_tax: getSkuPriceInEuros(sku) * quantity,
    value_incl_tax: getSkuPriceIncludingTax(sku) * quantity,
    voucher_discount_applied: sku.price?.voucherDiscountApplied,
    handling_charge: sku.shippingHandlingChargeAmount,
    duties: sku.shippingDutyAmount,
  });
}

export function sendViewCartEvent(cartItems: CartItem[]) {
  if (cartItems.length > 0) {
    const items = cartItems.map((cartItem) => {
      const { sku, quantity } = cartItem;
      if (!sku) return;
      return createGAItem(sku, quantity);
    });

    const value = cartItems.reduce((acc, cartItem) => {
      const { sku, quantity } = cartItem;
      if (!sku || !sku.price) return acc;

      const skuPrice = getSkuPrice(sku);
      return acc + skuPrice * (quantity || 0);
    }, 0);

    gtag("event", "view_cart", {
      currency: currentCurrencyCode,
      value,
      items,
    });
  }
}

export function sendViewItemListEvent(
  id: string,
  name: string,
  items: Gtag.Item[],
  customEventDimensions?: Gtag.CustomParams
) {
  gtag("event", "view_item_list", {
    currency: currentCurrencyCode,
    item_list_id: id,
    item_list_name: name,
    items,
    ...customEventDimensions,
  });
}

export function sendSelectItemEvent(
  id: string,
  name: string,
  items: Gtag.Item[],
  customEventDimensions?: Gtag.CustomParams
) {
  gtag("event", "select_item", {
    currency: currentCurrencyCode,
    item_list_id: id,
    item_list_name: name,
    items,
    ...customEventDimensions,
  });
}

export function sendRemoveFromCartEvent(sku: Sku | null, quantity: number) {
  if (!sku) return;
  gtag("event", "remove_from_cart", {
    currency: sku.price.currencyCode,
    value: getSkuPrice(sku) * quantity,
    items: [createGAItem(sku, quantity)],
    euro_value_incl_tax: getSkuPriceInEuros(sku) * quantity,
    value_incl_tax: getSkuPriceIncludingTax(sku) * quantity,
  });
}

function getSkuPrice(sku: Sku): number {
  const {
    price: { numericTotalExclTaxAfterDiscount, numericTotalExclTax },
  } = sku;

  return numericTotalExclTaxAfterDiscount
    ? numericTotalExclTaxAfterDiscount
    : numericTotalExclTax;
}

function getSkuPriceInEuros(sku: Sku): number {
  const {
    price: { eurValueForAnalytics, numericTotalInclTax },
  } = sku;

  return eurValueForAnalytics
    ? parseFloat(eurValueForAnalytics)
    : numericTotalInclTax;
}

function getSkuPriceIncludingTax(sku: Sku): number {
  const {
    price: { numericTotalInclTaxAfterDiscount, numericTotalInclTax },
  } = sku;

  return numericTotalInclTaxAfterDiscount
    ? numericTotalInclTaxAfterDiscount
    : numericTotalInclTax;
}

export function createGAItem(
  sku: Sku,
  quantity?: number,
  customItemDimensions?: Gtag.CustomParams
): Gtag.Item {
  const categories = Array(5)
    .fill(undefined)
    .map(
      (_, i) =>
        sku.analyticsData?.warehouseCategory?.facetingNameList?.[i] ?? undefined
    );

  const variantAttributeNames = sku.attributes
    .filter((attribute) => attribute?.variant)
    .map((attribute) => `${attribute.title} - ${attribute.value}`);

  // These properties are used for Google Ads Dynamic Remarketing https://support.google.com/google-ads/answer/7305793?hl=en
  const googleAdsProperties = {
    id: sku.productId,
    google_business_vertical: "retail",
  };

  const customDimensions = {
    euro_price_incl_tax: getSkuPriceInEuros(sku),
    price_incl_tax: getSkuPriceIncludingTax(sku),
    ...googleAdsProperties,
    ...customItemDimensions,
  };

  const { item_list_id, item_list_name } =
    getStoredItemListInfo(sku.productId.toString()) ?? {};

  return {
    item_id: sku.productId.toString(),
    item_name: sku.titleBeforeTranslation,
    discount: sku.price.numericTotalDiscount ?? undefined,
    item_brand: sku.brand.title,
    item_category: categories[0],
    item_category2: categories[1],
    item_category3: categories[2],
    item_category4: categories[3],
    item_category5: categories[4],
    item_variant: variantAttributeNames ? variantAttributeNames[0] : undefined,
    item_list_id,
    item_list_name,
    price: getSkuPrice(sku),
    quantity,
    ...customDimensions,
  };
}

export function mapProductTileToGtagItem(
  tile: HTMLElement,
  index?: number
): Gtag.Item | undefined {
  const { productId, name, category, price, eurPrice, discount } = tile.dataset;

  if (!productId) return;

  // For product lists, get the brand from the product tile to avoid creating another data attribute in the DOM
  let brand = tile.querySelector("[data-item-brand]")?.textContent;
  // For carousel items, get the brand from the data attributes
  if (!brand) brand = tile.dataset?.brand;

  const gtagItem: Gtag.Item = {
    item_id: productId,
    item_name: name,
    index,
    item_brand: brand,
    item_category: category,
    ...(price ? { price: parseFloat(price) } : {}),
    ...(discount ? { discount: parseFloat(discount) } : {}),
  };

  // For Carousels, add the retailer information
  const customParams: Gtag.CustomParams = {
    euro_price_incl_tax: eurPrice,
  };

  return {
    ...gtagItem,
    ...customParams,
  };
}

export function sendSearchEvent(query: string) {
  gtag("event", "search", {
    search_term: query,
  });
}

export function sendViewPromotionEvent(
  promotionId: string,
  promotionName: string,
  creativeName: string,
  creativeSlot: string
) {
  gtag("event", "view_promotion", {
    items: [
      {
        promotion_id: promotionId,
        promotion_name: promotionName,
        creative_name: creativeName,
        creative_slot: creativeSlot,
      },
    ],
  });
}

export function sendSelectPromotionEvent(
  promotionId: string,
  promotionName: string,
  creativeName: string,
  creativeSlot: string
) {
  gtag("event", "select_promotion", {
    items: [
      {
        promotion_id: promotionId,
        promotion_name: promotionName,
        creative_name: creativeName,
        creative_slot: creativeSlot,
      },
    ],
  });
}

export function sendClickEvent(
  content_id: string,
  content_name?: string,
  content_url?: string
) {
  gtag("event", "click", {
    content_id,
    ...(content_name ? { content_name } : {}),
    ...(content_url ? { content_url } : {}),
  });
}

export function sendViewContentEvent(
  content_id: string,
  content_name?: string
) {
  gtag("event", "view_content", {
    content_id,
    ...(content_name ? { content_name } : {}),
  });
}

const MAX_ITEM_LIST_ENTRIES = 10;
const ITEM_LIST_COOKIE_NAME = "ga_item_list_info";

export function saveItemListContext(
  item_id: string,
  item_list_id: string,
  item_list_name: string
) {
  const consents = getUserConsent() || defaultConsents;
  if (!consents.analytics) return;

  const cookieName = ITEM_LIST_COOKIE_NAME;
  const lifeTimeInDays = 7;

  let existing: ItemListContext[] = [];

  try {
    existing = JSON.parse(getCookie(cookieName) || "[]");
  } catch {} // eslint-disable-line no-empty -- malformed/missing cookie, fall back to empty list

  // Remove existing entry for same item
  existing = existing.filter((e) => e.item_id !== item_id);

  // Add newest to end
  existing.push({
    item_id,
    item_list_id,
    item_list_name,
  });

  // Trim oldest entries
  if (existing.length > MAX_ITEM_LIST_ENTRIES) {
    existing = existing.slice(-MAX_ITEM_LIST_ENTRIES);
  }

  setCookie(
    cookieName,
    JSON.stringify(existing),
    lifeTimeInDays,
    shiftDomain(location.hostname)
  );
}

export function getStoredItemListInfo(item_id: string) {
  const storedListInfo = getCookie(ITEM_LIST_COOKIE_NAME) ?? "[]";

  try {
    const listInfo = JSON.parse(storedListInfo) as ItemListContext[];
    return listInfo.find((l) => l.item_id === item_id) ?? null;
  } catch {} // eslint-disable-line no-empty -- malformed/missing cookie, fall through to null

  return null;
}
